import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import TranslationFallbackDiff, {
  type FallbackDiffRow,
} from "@/v2-backoffice-client/translation/fallback/TranslationFallbackDiff"
import TranslationFallbackMiniDiffCell from "@/v2-backoffice-client/translation/fallback/TranslationFallbackMiniDiffCell"
import { useTranslationFallbackQuery } from "@/v2-backoffice-client/translation/fallback/translation.fallback.query"
import { FALLBACK_MISSING_IN_DB } from "@/v2-backoffice-client/translation/fallback/translation.fallback.utils"

// One feature whose DB en-GB text diverges from the code catalog, with the
// field(s) that actually differ spelled out (a row can look identical in the
// Name columns while only the descriptions disagree).
type FallbackMismatchRow = FallbackDiffRow

// Which fields diverge. The sync compares exact values, so
// "identical-looking" diffs (NBSP vs space, curly vs straight quotes) still
// count — surfaced as "Invisible characters".
function deriveDiffersIn(mismatch: {
  MismatchType: string
  CodeTextName?: string | null
  FeatureName?: string | null
  CodeTextDescription?: string | null
  FeatureDescription?: string | null
}): string {
  if (mismatch.MismatchType === "Missing") return FALLBACK_MISSING_IN_DB
  const nameDiffers =
    (mismatch.CodeTextName ?? "") !== (mismatch.FeatureName ?? "")
  const descriptionDiffers =
    (mismatch.CodeTextDescription ?? "") !== (mismatch.FeatureDescription ?? "")
  if (nameDiffers && descriptionDiffers) return "Name + description"
  if (nameDiffers) return "Name"
  if (descriptionDiffers) return "Description"
  // Both fields compare equal after JSON round-trips: invisible-character
  // difference (NBSP, quote style) or a trailing-space variant.
  return "Invisible characters"
}

// Severity order for the "Differs in" column: a missing en-GB row is worse
// than a real text diff, which is worse than invisible-character noise.
const DIFFERS_IN_RANK: Record<string, number> = {
  [FALLBACK_MISSING_IN_DB]: 4,
  "Name + description": 3,
  Name: 2,
  Description: 1,
  "Invisible characters": 0,
}

const MISMATCH_COLUMNS = createColumns<FallbackMismatchRow>({
  enableSorting: true,
  columns: {
    // The status column leads: it is what the default sort ranks by and the
    // first thing an operator scans for.
    differsIn: {
      id: "differsIn",
      label: "Differs in",
      defaultSize: 150,
      sortingFn: (a, b) =>
        (DIFFERS_IN_RANK[a.original.differsIn] ?? 0) -
        (DIFFERS_IN_RANK[b.original.differsIn] ?? 0),
      enableGrouping: true,
      filter: {
        type: "select",
        placeholder: "Any difference",
        toolbar: true,
        options: Object.keys(DIFFERS_IN_RANK).map((kind) => ({
          label: kind,
          value: kind,
        })),
      },
    },
    code: { id: "code", label: "Code", defaultSize: 260 },
    name: {
      id: "name",
      label: "Name (− code / + DB)",
      description:
        "− code: the en-GB fallback in this repo's *.text.ts files (what the sync will overwrite). + DB: the database text that flows into the code. The published blobs are not involved here — they are the Publish output of the DB.",
      isWrapped: false,
      // Search/export still sees both variants.
      getValue: (row) => `${row.codeName} ${row.dbName}`,
      renderCell({ row }) {
        return (
          <TranslationFallbackMiniDiffCell
            code={row.original.codeName}
            db={row.original.dbName}
          />
        )
      },
    },
    description: {
      id: "description",
      label: "Description (− code / + DB)",
      description:
        "− code: the en-GB fallback in this repo's *.text.ts files (what the sync will overwrite). + DB: the database text that flows into the code.",
      isWrapped: false,
      getValue: (row) => `${row.codeDescription} ${row.dbDescription}`,
      renderCell({ row }) {
        return (
          <TranslationFallbackMiniDiffCell
            code={row.original.codeDescription}
            db={row.original.dbDescription}
          />
        )
      },
    },
  },
})

export type TranslationFallbackViewProps = Omit<
  TableProps<FallbackMismatchRow>,
  "data" | "columns"
>

// "Pending code sync": rows where the DB's en-GB text diverges from the code
// catalog (common.CodeTexts, kept in lockstep with the repo's *.text.ts by
// the text-sync workflow). Cells show a compact −/+ mini diff; clicking a row
// expands the full word-level diff with the resolve actions (keep DB = do
// nothing, or write the code text back into the DB). Contextual help lives
// in the sidebar item's info tooltip.
function TranslationFallbackView(props: TranslationFallbackViewProps) {
  const { className, ...tableProps } = props
  const { data, loading, error, refetch } = useTranslationFallbackQuery()
  errorToast(error, { title: "Failed to load pending code syncs" })

  const [expandedCode, setExpandedCode] = useState<string | null>(null)

  const rows: FallbackMismatchRow[] = (data?.getCodeTextMismatches ?? []).map(
    (mismatch) => ({
      code: mismatch.Code,
      differsIn: deriveDiffersIn(mismatch),
      codeName: mismatch.CodeTextName ?? "",
      dbName: mismatch.FeatureName ?? "",
      codeDescription: mismatch.CodeTextDescription ?? "",
      dbDescription: mismatch.FeatureDescription ?? "",
    }),
  )

  return (
    <Table
      className={cn("translation-fallback-table", className)}
      // v2: one-time layout reset so the "Differs in first" order also
      // reaches operators with a persisted (dragged) column order.
      tableId="translation-fallback-table-v2"
      data={rows}
      columns={MISMATCH_COLUMNS}
      // Worst drift first: missing en-GB rows, then real text diffs, then
      // invisible-character noise (see DIFFERS_IN_RANK).
      initialSorting={[{ id: "differsIn", desc: true }]}
      getRowId={(row) => row.code}
      enableGrouping
      enableInfiniteScroll
      enableRowSelection={false}
      onRowClick={(row) =>
        setExpandedCode((prev) =>
          prev === row.original.code ? null : row.original.code,
        )
      }
      expandedRowId={expandedCode}
      renderRowDetail={(row) => (
        <TranslationFallbackDiff
          key={row.original.code}
          row={row.original}
          onResolved={() => {
            setExpandedCode(null)
            void refetch()
          }}
        />
      )}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.Search placeholder="Search code or text..." />
        <div className="flex-1" />
        <Table.ToolbarFilters />
        <Table.ToolbarMenu exportFileName="pending-code-sync" />
      </Table.Toolbar>
      <Table.FilterList />
      <Table.GroupList />
      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default TranslationFallbackView
